import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

/**
 * Atom to manage the visibility state of the keyboard shortcuts modal
 */
export const keyboardHelpModalOpenAtom = atom(false);

/**
 * Atom to manage the visibility state of the command palette
 */
export const commandPaletteOpenAtom = atom(false);

/**
 * Controls the archive view toggle in the sidebar.
 * Set from the command palette, consumed by SidebarLayout.
 */
export const archiveViewAtom = atom(false);

/**
 * Signals the sidebar to enter rename mode for a specific environment.
 * Set to true from the command palette, consumed by the Env component in the sidebar.
 */
export const renameEnvironmentAtom = atomFamily((_environmentId: string) => atom(false));

/**
 * Holds a callback to toggle the details panel and its current open state.
 * Set by EnvironmentDetailsLayout when mounted, cleared on unmount.
 * Consumed by DesktopTitlebar to show a panel toggle button.
 */
export const detailsPanelToggleAtom = atom<{ toggle: () => void; isOpen: boolean } | null>(null);

/**
 * Holds the ID of the environment currently in rename mode, or null.
 * Only one environment can be renamed at a time — setting this to a new ID
 * automatically cancels the previous rename.
 */
export const renamingEnvironmentIdAtom = atom<string | null>(null);

/**
 * Monotonically increasing counter that signals the environment details view
 * to reveal the "Ports & Services" drawer tab. Incrementing the counter
 * (rather than using a boolean) ensures the effect fires even when the user
 * triggers the action multiple times in a row.
 *
 * Set from the command palette service start/stop actions, consumed by
 * EnvironmentDetailsPanel and SuperOnaEnvironmentDetailsPanel's BottomDrawer.
 */
export const revealServicesAtom = atom(0);

/**
 * Generic tab-switching signal for SuperOnaEnvironmentContent.
 * Producers increment `seq` with the desired tab name; the consumer
 * reacts to `seq` changes and reads `tab` to know which tab to activate.
 *
 * Using a monotonically increasing counter (rather than just the tab name)
 * ensures the effect fires even when the user triggers the same tab twice
 * in a row (e.g. re-clicking an already-selected file).
 */
export const superOnaContentTabAtom = atom<{ tab: "conversation" | "file" | "logs" | "environment"; seq: number }>({ tab: "conversation", seq: 0 });

/**
 * Tracks where the currently selected file was opened from in the right panel.
 * Used by SuperOnaConversationHeader for the tab label and by
 * SuperOnaFileDiffPanel to pass `baseRef` to diff hooks.
 *
 * - `changes`: uncommitted working tree changes (diff against HEAD)
 * - `branch-changes`: all changes on this branch vs the default branch (diff against origin/main)
 * - `all-files`: full directory listing (no diff, raw file view)
 */
export type SelectedFileSource =
    | { type: "changes" }
    | { type: "branch-changes"; baseRef: string }
    | { type: "all-files" };

export const selectedFileSourceAtom = atom<SelectedFileSource>({ type: "changes" });

/**
 * When set, the InlineCommentAnnotation matching this comment ID
 * should enter edit mode. Cleared after the annotation picks it up.
 */
export const editingCommentIdAtom = atom<string | null>(null);

/**
 * Holds the currently active log view opened from the bottom drawer.
 * When set, the main content area (SuperOnaEnvironmentContent) shows a
 * "Logs" tab with the corresponding service or task run logs.
 * Null means no log view is active.
 */
export type ActiveLogView =
    | { type: "service"; serviceId: string; environmentId: string }
    | { type: "task-run"; taskId: string; runId: string; environmentId: string };

export const activeLogViewAtom = atom<ActiveLogView | null>(null);
